import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppLayout from "@/components/AppLayout";
import TrustRing from "@/components/TrustRing";
import LifeAreaCard from "@/components/LifeAreaCard";
import LinaCard from "@/components/LinaCard";
import WelcomeOverlay from "@/components/WelcomeOverlay";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, ListChecks, AlertTriangle, CalendarClock, FileText } from "lucide-react";
import TrustHint from "@/components/TrustHint";

export default function DashboardPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [trust, setTrust] = useState({ total: 0, segments: {}, weekly_delta: 0 });
  const [areas, setAreas] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [weeklySentence, setWeeklySentence] = useState("");

  const load = () => {
    api.get("/trust-ring").then((r) => setTrust(r.data)).catch(() => {});
    api.get("/life-areas").then((r) => setAreas(r.data)).catch(() => {});
    api.get("/tasks", { params: { status: "open" } }).then((r) => setTasks(r.data.slice(0, 3))).catch(() => {});
    api.get("/documents/expiring", { params: { days: 90 } }).then((r) => setExpiring(r.data)).catch(() => {});
    api.get("/lina/today", { params: { lang } }).then((r) => setWeeklySentence(r.data?.weekly_summary?.sentence || "")).catch(() => {});
  };

  useEffect(() => {
    load();
    const onChange = (ev) => {
      if (ev.detail) setTrust(ev.detail);
      // Areas + tasks + expiring + lina (weekly) may have changed → reload
      api.get("/life-areas").then((r) => setAreas(r.data)).catch(() => {});
      api.get("/tasks", { params: { status: "open" } }).then((r) => setTasks(r.data.slice(0, 3))).catch(() => {});
      api.get("/documents/expiring", { params: { days: 90 } }).then((r) => setExpiring(r.data)).catch(() => {});
      api.get("/lina/today", { params: { lang } }).then((r) => setWeeklySentence(r.data?.weekly_summary?.sentence || "")).catch(() => {});
    };
    window.addEventListener("ag:trust-ring-changed", onChange);
    window.addEventListener("ag:tasks-changed", onChange);
    return () => {
      window.removeEventListener("ag:trust-ring-changed", onChange);
      window.removeEventListener("ag:tasks-changed", onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <AppLayout>
      <WelcomeOverlay />
      <header className="mb-10 ag-fade-in">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Mein Leben</div>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ag-ink leading-[1.05] mb-3" data-testid="dashboard-greeting">
          {t.dashboard.greeting(user?.first_name || "")}
        </h1>
        <p className="text-slate-600 max-w-xl text-lg">{t.dashboard.subline}</p>
        <div className="mt-5">
          <TrustHint testId="dashboard-trust-hint">{t.trustHints.dashboard}</TrustHint>
        </div>
      </header>

      <section className="grid lg:grid-cols-[1.1fr_1fr] gap-6 mb-12">
        <div className="ag-card p-8 lg:p-10 flex items-center justify-center ag-fade-in" style={{ animationDelay: "60ms" }}>
          <TrustRing total={trust.total} segments={trust.segments} weeklySentence={weeklySentence} />
        </div>
        <div className="space-y-5 ag-fade-in" style={{ animationDelay: "120ms" }}>
          <LinaCard />
          <div className="ag-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-serif text-xl text-ag-ink">{t.dashboard.openTasks}</div>
                <div className="text-xs text-slate-500 mt-0.5">{tasks.length} {tasks.length === 1 ? "Empfehlung" : "Empfehlungen"}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/aufgaben")} data-testid="dashboard-see-all-tasks" className="text-ag-ink hover:bg-slate-100">
                {t.dashboard.seeAll} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </div>
            {tasks.length === 0 ? (
              <div className="text-sm text-slate-500 py-4">{t.tasks.empty}</div>
            ) : (
              <ul className="space-y-2.5">
                {tasks.map((task) => (
                  <li key={task.id} data-testid={`dashboard-task-${task.id}`} className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${task.priority === "high" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>
                      <ListChecks className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ag-ink truncate">{task.title}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        <Clock className="h-3 w-3" /> {t.tasks.minutes(task.estimated_minutes)} · {task.category}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="ag-fade-in" style={{ animationDelay: "180ms" }}>
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">{t.dashboard.lifeTree}</div>
            <h2 className="font-serif text-3xl text-ag-ink">Ihre Lebensbereiche</h2>
          </div>
          <Link to="/lebensbaum" data-testid="dashboard-see-all-areas" className="text-sm text-ag-ink hover:underline">{t.dashboard.seeAll} →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {areas.map((a) => (
            <LifeAreaCard key={a.id} area={a} />
          ))}
        </div>
      </section>

      {expiring.length > 0 && (
        <section className="ag-fade-in mt-12" style={{ animationDelay: "220ms" }} data-testid="dashboard-expiring">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1 inline-flex items-center gap-2">
                <CalendarClock className="h-3 w-3" /> Bald ablaufend
              </div>
              <h2 className="font-serif text-3xl text-ag-ink">Aufmerksamkeit erforderlich</h2>
            </div>
            <Link to="/vault" className="text-sm text-ag-ink hover:underline">{t.dashboard.seeAll} →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {expiring.slice(0, 6).map((d) => {
              const tone = d.severity === "expired"
                ? { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", label: "Abgelaufen" }
                : d.severity === "urgent"
                ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", label: `${d.days_left} Tage` }
                : { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", label: `${d.days_left} Tage` };
              return (
                <Link to="/vault" key={d.id} data-testid={`expiring-${d.id}`} className={`ag-card ag-card-hover p-5 flex items-start gap-3 ${tone.bg} ${tone.border}`}>
                  <div className="h-10 w-10 rounded-xl bg-white/80 flex items-center justify-center shrink-0">
                    {d.severity === "expired"
                      ? <AlertTriangle className="h-5 w-5 text-red-600" strokeWidth={1.5} />
                      : <FileText className="h-5 w-5 text-ag-ink" strokeWidth={1.5} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs uppercase tracking-wider mb-0.5 ${tone.text}">{tone.label}</div>
                    <div className="font-serif text-lg text-ag-ink truncate">{d.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{new Date(d.expiry_date).toLocaleDateString(lang)}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </AppLayout>
  );
}
